/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Segment, Statistic } from 'semantic-ui-react'
import { UltyMoney } from '@ulty/ulty-ui'
import useUser from '../../Shared/UserProvider/useUser'
import { useTranslation } from 'react-i18next'
import { useDeviceMediaQuery } from '../../Shared/UseDeviceMediaQuery/useDeviceMediaQuery'
import { useQuery } from '@tanstack/react-query'
import { getUnavailableItemsSummary } from '../../../services/Item/Item.service'
import { useSearchParams } from 'react-router-dom-v5-compat'

const Summary = () => {
    const [t] = useTranslation()
    const {user} = useUser()
    const {isMobile} = useDeviceMediaQuery()
    const [searchParams] = useSearchParams()

    const from = searchParams.get('from')
    const to = searchParams.get('to')

    const {data: summary} = useQuery({
        queryFn: () => getUnavailableItemsSummary(from, to),
        queryKey: ['pointofsales', user.getUserPos()?.id, 'unavailableItemsSummary', from, to],
        enabled: !!from && !!to,
    })

    if (!summary || summary.totalMissedQuantity === 0) {
        return null
    }

    return (
      <Segment
        css={css`
            &.ui.segment {
                margin: 0;
            }
        `}
      >
          <div
            css={css`
                display: grid;
                grid-template-areas: ${isMobile ?`'a' 'd' 'c' 'b'` :
                        `"a b"
                        "c d"`};
                grid-template-columns: ${isMobile ? '1fr' : '1fr 1fr'};
                row-gap: .5rem;
            `}
          >
              <Statistic
                css={css`
                    grid-area: a;
                `}
                size="small"
              >
                  <Statistic.Value>{summary.totalMissedQuantity}</Statistic.Value>
                  <Statistic.Label>{t('unavailable_items.summary.total_quantity', { count: summary.totalMissedQuantity })}</Statistic.Label>
              </Statistic>

              <div
                css={css`
                    grid-area: c;
                    justify-self: center;
                    align-items: center;
                    font-size: 6pt;
                `}
              >
                  <Statistic size="mini">
                      <Statistic.Value>{summary.totalMissedSubstitutedQuantity}</Statistic.Value>
                      <Statistic.Label>{t('unavailable_items.summary.total_substituted_quantity', { count: summary.totalMissedSubstitutedQuantity })}</Statistic.Label>
                  </Statistic>
              </div>


              <div
                css={css`
                    grid-area: d;
                    justify-self: center;
                    align-items: center;
                    font-size: 6pt;
                `}>
                  <Statistic
                    color='red'
                    size="mini"
                  >
                      <Statistic.Value>
                          {summary.totalMissedRevenueUnavailable > 0 ? (
                            <UltyMoney
                              readonly
                              inline
                              noMargin
                              amount={summary.totalMissedRevenueUnavailable}
                              currencyCode={user.getCompanyCurrency()}
                              locale={user.getCompanyLocale()}
                            />
                          ) : '-'}
                      </Statistic.Value>
                      <Statistic.Label>{t('unavailable_items.summary.total_revenue_unavailable')}</Statistic.Label>
                  </Statistic>

                  <Statistic
                    color='green'
                    size="mini"
                  >
                      <Statistic.Value>
                          {summary.totalMissedRevenueSubstituted > 0 ? (
                            <UltyMoney
                              readonly
                              inline
                              noMargin
                              amount={summary.totalMissedRevenueSubstituted}
                              currencyCode={user.getCompanyCurrency()}
                              locale={user.getCompanyLocale()}
                            />
                          ) : '-'}
                      </Statistic.Value>
                      <Statistic.Label>{t('unavailable_items.summary.total_revenue_substituted')}</Statistic.Label>
                  </Statistic>
              </div>


              <Statistic
                css={css`
                    grid-area: b;
                    margin: 0 !important;
                `}
                size="small"
              >
                  <Statistic.Value>
                      {summary.totalMissedRevenue > 0 ? (
                        <UltyMoney
                          readonly
                          inline
                          noMargin
                          amount={summary.totalMissedRevenue}
                          currencyCode={user.getCompanyCurrency()}
                          locale={user.getCompanyLocale()}
                        />
                      ) : '-'}
                  </Statistic.Value>
                  <Statistic.Label>{t('unavailable_items.summary.total_revenue')}</Statistic.Label>
              </Statistic>
          </div>
      </Segment>
    )
}

export { Summary }
